import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import PlusIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import { type SelectOption } from '@/components/Select/Select'
import Table from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { useSorting } from '@/hooks/useSorting'
import { studentsQueryOptions } from '@/queries/studentsQueryOptions'
import useSemesters from '@/queries/useSemesters'
import useAuthStore from '@/store/useAuthStore'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { getColumnWidth } from '@/utils/get-column-width'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import type { AbsencesAllFiltersKeys } from '../constants/absences-all-filters'
import useAbsencesDownload from '../mutations/useAbsencesDownload'
import { absencesClassesQueryOptions } from '../queries/absencesClassesQueryOptions'
import useStudentAbsences, {
  type StudentAbsence
} from '../queries/useStudentAbsences'
import useStudentAbsencesCount from '../queries/useStudentAbsencesCount'

const routeApi = getRouteApi('/_auth/absences/all')

const AbsencesAllView = () => {
  const { t } = useTranslation('absences2')
  const { isSuperAdmin } = useAuthStore()

  const enabledPlanAbsence = useFeatureFlag('IS_PLAN_ABSENCES_MODULE_ENABLED')

  const navigate = routeApi.useNavigate()
  const { semester, page, pageSize, students, classes, sort, order } =
    routeApi.useSearch()

  const { isMobile, isExtraLarge } = useScreenResolution()

  const searchParams = {
    pageSize,
    page,
    semester,
    classId: classes?.map(getValue),
    studentId: students?.map(getValue),
    order,
    sort
  }

  const { data: studentAbsences, isFetching: isStudentAbsencesLoading } =
    useStudentAbsences(searchParams)

  const { data: studentAbsencesCount } = useStudentAbsencesCount({
    semesterId: semester
  })

  const { data: semesters, isLoading: isSemestersLoading } = useSemesters()

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isSemestersLoading,
        options: semesters?.semestersFromCurrentYearOptions || [],
        semesterList: semesters?.list || [],
        value: semester,
        onChange: value => {
          navigate({
            search: {
              semester: value,
              page: 1,
              pageSize: DEFAULT_PAGE_SIZE
            }
          })
        }
      }
    },
    {
      label: t('label.student'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'student',
        multiple: true,
        value: students,
        queryOptions: search =>
          studentsQueryOptions({
            search,
            semesterId: semester
          }),
        placeholder: t('label.student'),
        onChange: value => handleChangeFilter('students', value)
      }
    },
    {
      label: t('label.class'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'classId',
        multiple: true,
        value: classes,
        queryOptions: search =>
          absencesClassesQueryOptions({
            search,
            semesterId: semester
          }),
        placeholder: t('label.class'),
        onChange: value => handleChangeFilter('classes', value)
      }
    }
  ]

  const handleChangeFilter = (
    key: AbsencesAllFiltersKeys,
    value?: SelectOption<string>[]
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const isSomeFilterSelected = checkIfFilterSelected(filters, ['semester'])

  const { columns, ...manageColumns } = useColumns<StudentAbsence>(
    [
      {
        key: 'name',
        label: t('header.student'),
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        render: (value: StudentAbsence) => (
          <LinkText
            to="/students-and-classes/students/$studentId/details"
            params={{
              studentId: value.id
            }}
          >
            {value.name}
          </LinkText>
        ),
        sorterKey: 'full_name'
      },
      {
        key: 'student_class',
        label: t('header.class'),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        render: (value: StudentAbsence) =>
          value.studentClass && value.studentClass.id ? (
            <LinkText
              to="/students-and-classes/classes/$classId/details"
              params={{
                classId: value.studentClass.id
              }}
            >
              {value.studentClass.displayName}
            </LinkText>
          ) : (
            value.studentClass?.displayName
          ),
        sorterKey: 'class_name'
      },
      {
        dataIndex: 'excusedLessonsCount',
        key: 'excused_lessons_count',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        label: t('header.excused'),
        sorterKey: 'excused_lessons_count'
      },
      {
        dataIndex: 'unexcusedLessonsCount',
        key: 'unexcused_lessons_count',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        label: t('header.unexcused'),
        sorterKey: 'unexcused_lessons_count'
      },
      {
        dataIndex: 'totalAbsencesCount',
        key: 'total_absences_count',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        label: t('header.total-absences'),
        sorterKey: 'total_absences_count'
      },
      {
        dataIndex: 'latenessLessonsCount',
        key: 'lateness_lessons_count',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        label: t('header.late'),
        sorterKey: 'lateness_lessons_count'
      }
    ],
    { routeApi }
  )

  const download = useAbsencesDownload({
    ...searchParams,
    columnsKey: manageColumns.activeColumnKeys
  })

  const handleClearAll = () => {
    navigate({
      search: {
        semester,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const sorting = useSorting(routeApi)
  const pagination = usePagination(routeApi)

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />
            {isSuperAdmin && enabledPlanAbsence ? (
              <Button
                asLink
                to="/absences/plan"
                search={{ semester }}
                icon={<PlusIcon />}
              >
                {t('button.plan-absences')}
              </Button>
            ) : undefined}
          </>
        }
        manageColumns={manageColumns}
      />

      <Table
        id="absences-all-table"
        isLoading={isStudentAbsencesLoading}
        data={studentAbsences?.list || []}
        columns={columns}
        count={studentAbsences?.count}
        totalCount={studentAbsencesCount}
        {...pagination}
        {...sorting}
        caption={t('header.absences')}
      />
    </>
  )
}
export default AbsencesAllView
